/**
 * Admin API Keys service (super-admin only).
 *
 * Backend: user-identity service at /api/v1/api-keys (see localhost:17979/api-docs).
 * Full URL: {origin}/api/user-identity/api/v1/api-keys (proxy strips /api/user-identity).
 *
 * Uses the same user-identity API service as permissions/roles so the admin token is sent.
 */
import { permissionsRolesApiService } from './permissionsRolesApi';
import type {
  ApiKey,
  ApiKeyCreateRequest,
  ApiKeyUpdateRequest,
  ApiKeyValidateResponse,
} from '@/types/apiKey';

/** Backend path (no /api/user-identity prefix). From user-identity api-docs (localhost:17979/api-docs). */
const BASE = '/api/v1/api-keys';

function unwrap<T>(raw: { data?: T } | T): T {
  if (raw && typeof raw === 'object' && 'data' in raw && (raw as { data?: T }).data !== undefined) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

/** Normalize backend ApiKeyResponse (serviceName, expiresAt, keyValue) to our ApiKey shape. */
function normalizeKey(raw: Record<string, unknown>): ApiKey {
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? raw.serviceName ?? ''),
    keyPrefix: typeof raw.keyPrefix === 'string' ? raw.keyPrefix : (typeof raw.keyValue === 'string' ? raw.keyValue : undefined),
    plainKey: typeof raw.plainKey === 'string' ? raw.plainKey : undefined,
    description: typeof raw.description === 'string' ? raw.description : undefined,
    validUntil: typeof raw.validUntil === 'string' ? raw.validUntil : (typeof raw.expiresAt === 'string' ? raw.expiresAt : undefined),
    active: raw.active !== false,
    lastUsedAt: typeof raw.lastUsedAt === 'string' ? raw.lastUsedAt : undefined,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : undefined,
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : undefined,
    createdBy: typeof raw.createdBy === 'string' ? raw.createdBy : undefined,
  };
}

export const apiKeysApi = {
  /** List all API keys (paginated). Backend: GET /api/v1/api-keys?page=&size= */
  list: async (params?: { page?: number; size?: number }): Promise<{ content: ApiKey[]; page?: { totalElements: number; totalPages: number } }> => {
    const query = new URLSearchParams();
    if (params?.page != null) query.set('page', String(params.page));
    if (params?.size != null) query.set('size', String(params.size));
    const url = query.toString() ? `${BASE}?${query}` : BASE;
    const response = await permissionsRolesApiService.get<{ content: unknown[]; page?: { totalElements?: number; totalPages?: number } } | unknown[]>(url);
    const data = unwrap(response);
    if (Array.isArray(data)) {
      const content = data.map((k) => normalizeKey(typeof k === 'object' && k !== null ? (k as Record<string, unknown>) : { id: '' }));
      return { content, page: { totalElements: content.length, totalPages: 1 } };
    }
    const obj = data as { content?: unknown[]; page?: { totalElements?: number; totalPages?: number } };
    const content = Array.isArray(obj?.content)
      ? obj.content.map((k) => normalizeKey(typeof k === 'object' && k !== null ? (k as Record<string, unknown>) : { id: '' }))
      : [];
    return { content, page: obj?.page ? { totalElements: obj.page.totalElements ?? content.length, totalPages: obj.page.totalPages ?? 1 } : undefined };
  },

  /** Create a new API key. Backend: POST /api/v1/api-keys (requires serviceName, environment, scopes). */
  create: async (body: ApiKeyCreateRequest): Promise<ApiKey> => {
    const payload = {
      serviceName: body.name,
      environment: 'production',
      scopes: ['*'],
      description: body.description ?? undefined,
      expiresInDays: body.validUntil ? Math.ceil((new Date(body.validUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : undefined,
    };
    const response = await permissionsRolesApiService.post<ApiKey | Record<string, unknown> | { data: ApiKey }>(BASE, payload);
    const raw = unwrap(response);
    return normalizeKey(typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : { id: '' });
  },

  /** Get one API key by id. Backend: GET /api/v1/api-keys/{keyId} */
  getById: async (id: string): Promise<ApiKey> => {
    const response = await permissionsRolesApiService.get<ApiKey | Record<string, unknown> | { data: ApiKey }>(`${BASE}/${id}`);
    const raw = unwrap(response);
    return normalizeKey(typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : { id: '' });
  },

  /** Update an API key. Backend: PUT /api/v1/api-keys/{keyId} (body: serviceName, environment, scopes). */
  update: async (id: string, body: ApiKeyUpdateRequest): Promise<ApiKey> => {
    const payload = {
      serviceName: body.name ?? undefined,
      environment: 'production',
      scopes: ['*'],
      description: body.description ?? undefined,
      expiresInDays: body.validUntil ? Math.ceil((new Date(body.validUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : undefined,
    };
    const response = await permissionsRolesApiService.put<ApiKey | Record<string, unknown> | { data: ApiKey }>(`${BASE}/${id}`, payload);
    const raw = unwrap(response);
    return normalizeKey(typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : { id: '' });
  },

  /**
   * Validate an API key. Backend expects POST /api/v1/api-keys/validate with X-API-Key header.
   * When we only have key id we cannot send the secret; we call with empty header and the backend may return invalid.
   */
  validate: async (_id: string, keyValue?: string): Promise<ApiKeyValidateResponse> => {
    const headers = keyValue ? { 'X-API-Key': keyValue } : undefined;
    const response = await permissionsRolesApiService.post<ApiKeyValidateResponse | { data: ApiKeyValidateResponse }>(
      `${BASE}/validate`,
      {},
      { headers }
    );
    return unwrap(response);
  },

  /** Revoke (deactivate) an API key. Backend: DELETE /api/v1/api-keys/{keyId} */
  revoke: async (id: string): Promise<void> => {
    await permissionsRolesApiService.delete(`${BASE}/${id}`);
  },
};
